import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Palette, Loader2, Check, X, Undo2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PALETTES } from "@/lib/chartTheme";
import { describeTheme } from "@/lib/aiTheme";

// Item 101 — "Ask AI" restyling. Ask for a look, see it live on the canvas, then Apply or Discard.
// Applying pushes a single undo step, so one click puts the old look back.

const IDEAS = [
  "Make this warm oranges",
  "Give the dashboard a dark navy theme",
  "Use corporate blues and a white canvas",
  "Soft pastel palette, rounded cards",
  "High-contrast monochrome for printing",
];

export function AiThemePanel({ scope, onScopeChange, widget, canvas, onPreview, onApply, onDiscard, onUndo, preview, canUndo }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (instruction) => {
    const ask = (instruction || text).trim();
    if (!ask) return;
    if (scope === "widget" && !widget) { toast.error("Select a widget first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/theme", {
        instruction: ask,
        scope,
        canvas: { bg_color: canvas?.bg_color || "", chart_font: canvas?.chart_font || "" },
        widget: scope === "widget" && widget
          ? { type: widget.type, title: widget.title, options: (widget.config || {}).options || {} }
          : null,
        palettes: Object.keys(PALETTES),
      });
      onPreview({ ...data, widget_id: scope === "widget" ? widget?.id : null, instruction: ask });
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The assistant could not restyle that");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="ai-theme-panel" className="glass-card p-3.5 space-y-2.5">
      <div className="flex items-center gap-2">
        <Palette className="w-4 h-4 text-[color:var(--brand-ocean)]" />
        <span className="text-sm font-medium text-[color:var(--ink)] flex-1">Restyle with AI</span>
        <Select value={scope} onValueChange={onScopeChange}>
          <SelectTrigger data-testid="ai-theme-scope" className="h-7 w-[132px] text-[11px] bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="dashboard">Whole dashboard</SelectItem>
            <SelectItem value="widget">Selected widget</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {scope === "widget" && (
        <p className="text-[11px] text-[color:var(--ink-3)]">
          {widget ? <>Styling <span className="text-[color:var(--ink-2)]">{widget.title || widget.type}</span></> : "Select a widget on the canvas first."}
        </p>
      )}

      <div className="flex gap-1.5">
        <Input data-testid="ai-theme-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") run(); }}
          placeholder={scope === "widget" ? "e.g. warm oranges with a soft border" : "e.g. dark navy theme with mint accents"}
          className="h-8 text-[12px] bg-[color:var(--surface)]" />
        <Button data-testid="ai-theme-run" size="sm" onClick={() => run()} disabled={busy} className="h-8 bg-[color:var(--brand-ocean)] text-white">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {!preview && (
        <div className="flex flex-wrap gap-1.5">
          {IDEAS.map((s) => (
            <button key={s} data-testid={`ai-theme-idea-${s.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`} onClick={() => run(s)}
              className="text-[10px] px-2 py-1 rounded-full border border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:text-[color:var(--brand-ocean)]">
              {s}
            </button>
          ))}
        </div>
      )}

      {preview && (
        <div data-testid="ai-theme-preview" className="rounded-xl border border-[color:var(--brand-ocean)]/40 bg-[color:var(--surface)] p-2.5 space-y-2">
          <p className="text-[11px] text-[color:var(--ink-2)]">{preview.reply || "Preview applied to the canvas"}</p>
          {preview.colors?.length ? (
            <div className="flex gap-1">
              {preview.colors.map((c) => <span key={c} title={c} className="w-4 h-4 rounded" style={{ background: c }} />)}
            </div>
          ) : null}
          <p className="text-[10px] text-[color:var(--ink-3)]">{describeTheme(preview)}</p>
          <div className="flex gap-1.5">
            <Button data-testid="ai-theme-apply" size="sm" onClick={onApply} className="h-7 flex-1 text-[11px] bg-[color:var(--brand-mint)] text-white">
              <Check className="w-3.5 h-3.5 mr-1" />Apply
            </Button>
            <Button data-testid="ai-theme-discard" size="sm" variant="outline" onClick={onDiscard} className="h-7 text-[11px]">
              <X className="w-3.5 h-3.5 mr-1" />Discard
            </Button>
          </div>
        </div>
      )}

      {!preview && canUndo && (
        <Button data-testid="ai-theme-undo" size="sm" variant="outline" onClick={onUndo} className="h-7 w-full text-[11px]">
          <Undo2 className="w-3.5 h-3.5 mr-1" />Undo the AI restyle
        </Button>
      )}
    </div>
  );
}
